import React, { useState } from 'react';
import { X, AlertCircle, ArrowDownLeft } from 'lucide-react';
import { PAYMENT_MODES, partnerAccountingService } from '../../../services/partnerAccountingService';

export default function ReversementModal({ provider, onClose, onSuccess }) {
  const [form, setForm] = useState({
    amountReversed: '',
    reversalDate: new Date()?.toISOString()?.split('T')?.[0],
    paymentMode: 'especes',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    const amount = Number(form?.amountReversed);
    if (!amount || amount <= 0) {
      setError('Le montant reversé doit être supérieur à 0.');
      return;
    }
    if (provider?.remainingBalance && amount > provider?.remainingBalance) {
      setError(`Le montant ne peut pas dépasser le solde restant (${provider?.remainingBalance?.toFixed(2)}€).`);
      return;
    }
    setSaving(true);
    try {
      await partnerAccountingService?.createReversement({
        providerId: provider?.providerId,
        providerName: provider?.providerName,
        amountReversed: amount,
        reversalDate: form?.reversalDate,
        paymentMode: form?.paymentMode,
        notes: form?.notes,
      });
      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Erreur lors de l\'enregistrement du reversement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ArrowDownLeft className="h-5 w-5 text-green-600" />
              Enregistrer un reversement
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {provider?.providerName}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Remaining balance info */}
          {provider?.remainingBalance > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                Solde restant dû : <span className="font-bold">{provider?.remainingBalance?.toFixed(2)}€</span>
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Montant reversé (€) *
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form?.amountReversed}
              onChange={e => setForm(f => ({ ...f, amountReversed: e?.target?.value }))}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date du reversement *
            </label>
            <input
              type="date"
              value={form?.reversalDate}
              onChange={e => setForm(f => ({ ...f, reversalDate: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Mode de paiement *
            </label>
            <select
              value={form?.paymentMode}
              onChange={e => setForm(f => ({ ...f, paymentMode: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500"
            >
              {PAYMENT_MODES?.map(m => (
                <option key={m?.value} value={m?.value}>{m?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Note
            </label>
            <textarea
              value={form?.notes}
              onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
              rows={2}
              placeholder="Informations complémentaires..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            Le montant sera automatiquement affecté aux opérations les plus anciennes non soldées.
          </p>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving ? (
                <><span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enregistrement...</>
              ) : (
                <><ArrowDownLeft className="h-4 w-4" />Enregistrer le reversement</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
